import { notificationStore, EventTrigger, DeliveryChannel, DeliveryStatus } from '@/pages/Notification/notificationStore'

interface TriggerPayload {
  event: EventTrigger
  title: string
  message: string
  recipient: string
  channels?: DeliveryChannel[] // Override default channels if needed
}

export class NotificationEngine {
  /**
   * Checks if current time is within office hours (Mon-Fri, 09:00 - 18:00)
   */
  static isOfficeHours(date: Date = new Date()): boolean {
    const day = date.getDay()
    const hours = date.getHours()
    
    // Weekend check (0 = Sunday, 6 = Saturday)
    if (day === 0 || day === 6) {
      return false
    }
    
    // Office hours check: 9 AM (9) to 6 PM (18)
    if (hours >= 9 && hours < 18) {
      return true
    }
    
    return false
  }

  /**
   * Triggers a system-wide notification.
   * Handles delivery scheduling based on office hours.
   */
  static trigger(payload: TriggerPayload) {
    const { event, title, message, recipient, channels } = payload
    
    // Default to Email and WhatsApp as per NT-01 if not specified
    const targetChannels: DeliveryChannel[] = channels || ['In-App', 'Email', 'WhatsApp']
    
    // Evaluate delivery status (NT-03: Real-time, office hours only)
    const canSendNow = this.isOfficeHours()
    const status: DeliveryStatus = canSendNow ? 'Sent' : 'Queued'

    // Dispatch to store
    notificationStore.save({
      eventTrigger: event,
      title,
      message,
      recipient,
      channels: targetChannels,
      status,
      createdAt: new Date().toISOString()
    })

    // Console logging to simulate third-party API dispatches (e.g., Twilio/SendGrid)
    if (canSendNow) {
      console.log(`[Notification Engine] 🚀 Dispatched real-time via [${targetChannels.join(', ')}]`)
      console.log(`[Notification Engine] To: ${recipient} | Event: ${event}`)
    } else {
      console.log(`[Notification Engine] ⏳ Queued for office hours. To: ${recipient} | Event: ${event}`)
    }
  }

  /**
   * Utility to manually process queued messages (simulates a cron job)
   */
  static processQueue() {
    if (!this.isOfficeHours()) {
      console.log(`[Notification Engine] Cannot process queue outside office hours.`)
      return
    }

    const items = notificationStore.getAll()
    const queued = items.filter(i => i.status === 'Queued')
    
    if (queued.length === 0) return

    console.log(`[Notification Engine] Processing ${queued.length} queued messages...`)
    
    queued.forEach(item => {
      notificationStore.save({ ...item, status: 'Sent' })
      console.log(`[Notification Engine] 🚀 Sent queued message [${item.id}] via [${item.channels.join(', ')}]`)
    })
  }
}
